import CreateSetupBackend from "../config/config.js";
import SetupAuth from "../config/configAuth.js";
import accountManagerService from "./accountManagerService.js";
import { isMutual } from "../../utils/isMutual.jsx";
import {
  persistServicesEnable,
  persistEnableLegacy,
} from "../../utils/servicesEnable.js";

let SetupBackend = null;

function decodeQrCode(data) {
  SetupBackend = CreateSetupBackend();
  if (isMutual()) {
    return SetupBackend.post("/v1/baas/util/decodeqrcode", data);
  }
  return SetupBackend.post("/v1/pix/util/decodeqrcode", data);
}

function showPix(data) {
  SetupBackend = CreateSetupBackend();
  return SetupBackend.get(`/v1/pix/util/showpix?id=${data.id}`);
}

function showPixKey(key) {
  SetupBackend = CreateSetupBackend();
  return SetupBackend.get(
    `/v1/pix/util/showpixkey?email=${sessionStorage.getItem(
      "emailUser",
    )}&key=${key}`,
  );
}

function showExternalPixKey(data) {
  SetupBackend = CreateSetupBackend();
  if (isMutual()) {
    return SetupBackend.get(
      `/v1/baas/accounts/keys/resolve?email=${sessionStorage.getItem("emailUser")}&key=${encodeURIComponent(data.key || "")}`,
    );
  }
  return SetupBackend.get(
    `/v1/pix/util/showpixkey/${data.account}?key=${data.key}`,
  );
}

function saveSessionFromAccountStatus(accountStatus, email) {
  const account = accountStatus?.data?.body?.account || {};

  if (email) sessionStorage.setItem("emailUser", email);
  if (account?.documentNumber)
    sessionStorage.setItem("doc", account.documentNumber);
  if (account?.account) sessionStorage.setItem("account", account.account);
  if (account?.branch) sessionStorage.setItem("agency", account.branch);
  if (account?.name) {
    sessionStorage.setItem("nameUser", account.name);
    sessionStorage.setItem("fantasyName", account.name);
  }
}

function saveBaasBearerToken(userData) {
  const token = userData?.baasBearerToken;
  if (token) {
    sessionStorage.setItem("baasBearerToken", token);
  } else {
    // Evita reutilizar token antigo/stale entre sessões quando API retorna null.
    sessionStorage.removeItem("baasBearerToken");
  }
}

// Correção rodada 1 (Task 23): lista vazia é decisão do backend ("nada liberado"), não
// ausência de informação. Antes, `raw` sempre virava `[]` por causa do `?? []` no fim da
// cadeia — o mesmo `[]` tanto para "servicesEnable: []" quanto para "campo nenhum enviado" —
// então uma revogação de verdade caía no fallback de `enable` e podia reabrir a tela para
// quem não tem mais direito. Agora só cai no fallback quando os três campos estão
// realmente ausentes (`raw === undefined`); array explícito, mesmo vazio, vale como está.
function resolveServiceFlags(userData) {
  const raw =
    userData?.services_enable ?? userData?.servicesEnable ?? userData?.services;

  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === "string" && raw.trim().length > 0) {
    return [raw.trim().toUpperCase()];
  }

  // O backend mandou algo (ex.: string vazia) que não virou lista utilizável — ainda é
  // resposta explícita, não ausência. Não cai no fallback de `enable`.
  if (raw !== undefined) {
    return [];
  }

  const enable = String(userData?.enable || "").toUpperCase();
  if (enable === "BAAS" || enable === "RAMPS" || enable === "ALL") {
    return [enable];
  }

  return [];
}

async function getJWT() {
  const response = await SetupAuth.post("/v1/authentication/login");

  if (!response.status === 200) {
    throw new Error("Login failed");
  }

  const data = response.data;
  sessionStorage.setItem("token", `Bearer ${data}`);
}

async function login(email, password, meta, twoFactorCode) {
  try {
    await getJWT();
    let user = await accountManagerService.login({
      email: email,
      password: password,
      meta: JSON.stringify(meta),
      twoFactorCode: twoFactorCode || undefined,
    });

    saveBaasBearerToken(user?.data);

    if (user.data && user.data.requires2FA) {
      sessionStorage.setItem("tempPassword", password);
      return {
        requires2FA: true,
        tempToken: user.data.tempToken,
        email: email,
      };
    }

    const userHas2FA = user.data && user.data.body && user.data.body.setting2fa;

    if (!userHas2FA) {
      sessionStorage.setItem("tempPassword", password);
      sessionStorage.setItem("tempUserEmail", email);
      return {
        requiresSetup: true,
        userData: user.data,
      };
    }

    sessionStorage.setItem("tempPassword", password);
    return {
      requires2FA: true,
      email: email,
      userData: user.data,
    };
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
}

async function validateAuth(email, password) {
  try {
    await getJWT();
    await accountManagerService.login({
      email: email,
      password: password,
    });
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
}

async function verify2FA(email, code, password, meta) {
  try {
    await getJWT();
    const passwordToUse = password || sessionStorage.getItem("tempPassword");

    const loginResponse = await accountManagerService.login({
      email: email,
      password: passwordToUse,
      twoFactorCode: code,
      meta: meta ? JSON.stringify(meta) : undefined,
    });

    saveBaasBearerToken(loginResponse?.data);

    const enableMode = String(loginResponse?.data?.enable || "").toUpperCase();
    const baasToken = loginResponse?.data?.baasBearerToken;
    if (enableMode === "BAAS" && !baasToken) {
      throw new Error("BAAS_TOKEN_MISSING");
    }

    if (loginResponse.data && loginResponse.status === 200) {
      const accountStatus = await accountManagerService.showAccountStatus({
        email: email,
      });

      console.log("User data after 2FA verification:", accountStatus);

      // data
      // :
      // bankAccountData
      // :
      // {ispb: '60701190', branchNumber: '0001', accountNumber: '26530252', availableBalance: null, blockedBalance: null, …}
      // birthdate
      // :
      // "1990-01-01"
      // country
      // :
      // "BRA"
      // document
      // :
      // number
      // :
      // "12345678000199"
      // type
      // :
      // "CNPJ"
      // [[Prototype]]
      // :
      // Object
      // email
      // :
      // "john.doe@mailinator.com"
      // fullName
      // :
      // "Company LTDA"
      // holderType
      // :
      // "ORGANIZATION"
      // id
      // :
      // "08e49e74-86cf-4ac4-80b4-42e573d79dee"
      // phone
      // :
      // "+5511912349865"
      // status
      // :
      // "ACTIVE"

      sessionStorage.setItem("emailUser", email);
      // Grava sempre, inclusive lista vazia (correção rodada 1 da Task 23): o valor
      // anterior de outra sessão/cliente não pode sobreviver a um login que não libera
      // nada. persistServicesEnable (rodada 2) escreve incondicionalmente — não tem guarda
      // nenhuma para um refactor futuro reintroduzir por engano.
      persistServicesEnable(resolveServiceFlags(loginResponse?.data));
      // Task 30: `enable` legado numa chave própria, desacoplada de services_enable —
      // isMutual() passa a ler daqui.
      persistEnableLegacy(loginResponse?.data?.enable);
      sessionStorage.setItem("type", "");
      sessionStorage.setItem("USD", "");
      sessionStorage.setItem("EUR", "");
      sessionStorage.setItem("JPY", "");
      sessionStorage.setItem("CAD", "");
      const expirationTime = new Date().getTime() + 30 * 60 * 1000;
      sessionStorage.setItem("sessionExpiration", expirationTime.toString());

      sessionStorage.removeItem("tempPassword");

      saveSessionFromAccountStatus(accountStatus, email);

      if (loginResponse.data.enable !== "RAMPS") {
        const bank = await accountManagerService.showBalance();
        const accountFromBalance =
          bank?.data?.account || bank?.data?.body?.account;
        const agencyFromBalance =
          bank?.data?.agency || bank?.data?.body?.agency;
        if (accountFromBalance) {
          sessionStorage.setItem("account", accountFromBalance);
        }
        if (agencyFromBalance) {
          sessionStorage.setItem("agency", agencyFromBalance);
        }
      }
      return loginResponse.data;
    }
    throw new Error("Invalid 2FA verification");
  } catch (error) {
    console.error("Error during 2FA verification:", error);
    throw error;
  }
}

function checkSessionExpiration() {
  const expiration = sessionStorage.getItem("sessionExpiration");
  if (!expiration) return;

  const now = new Date().getTime();
  if (now > parseInt(expiration, 10)) {
    sessionStorage.setItem("sessionExpiration", "");
    logout();
  }
}

function logout() {
  sessionStorage.setItem("token", "");
  sessionStorage.setItem("baasBearerToken", "");
  sessionStorage.setItem("nameUser", "");
  sessionStorage.setItem("doc", "");
  sessionStorage.setItem("account", "");
  sessionStorage.setItem("emailUser", "");
  sessionStorage.setItem("fantasyName", "");
  sessionStorage.setItem("type", "");
  sessionStorage.setItem("documentscopys", "");
  sessionStorage.setItem("status", "");
  // Correção rodada 1 (Task 23): faltava limpar a liberação por produto — sobrevivia ao
  // logout e vazava para o próximo login na mesma aba (isMutual.jsx também lê essa chave).
  // Rodada 2: usa persistServicesEnable como os outros pontos de escrita, em vez de um
  // setItem cru — getServicesEnable lê "[]" do jeito que lê "", então o comportamento não
  // muda.
  persistServicesEnable([]);
  // Task 30: mesma limpeza para o `enable` legado — sem isso, um cliente herdaria o
  // comportamento de Pix do cliente anterior na mesma máquina (isMutual.jsx também lê essa
  // chave).
  persistEnableLegacy("");
  window.location.href = "/login";
}

async function createMFA(email, password) {
  try {
    await getJWT();

    if (!sessionStorage.getItem("baasBearerToken")) {
      const loginResponse = await accountManagerService.login({
        email: email,
        password: password,
      });
      saveBaasBearerToken(loginResponse?.data);
    }

    const response = await accountManagerService.createMFA({
      email: email,
      password: password,
    });

    if (response.data && response.data.token && response.data.token.result) {
      return {
        otpauthUrl: response.data.token.result,
        success: true,
      };
    }

    throw new Error("Failed to create MFA");
  } catch (error) {
    console.error("Error creating MFA:", error);
    throw error;
  }
}

async function verifyMFASetup(email, code) {
  try {
    await getJWT();
    const response = await accountManagerService.verifyMFA({
      email: email,
      code: code,
    });

    if (response.data && response.status === 200) {
      return response.data;
    }
    throw new Error("Invalid 2FA verification");
  } catch (error) {
    console.error("Error during 2FA setup verification:", error);
    throw error;
  }
}

async function changePasswordWith2FA(
  currentPassword,
  newPassword,
  twoFactorCode,
) {
  try {
    await getJWT();
    const email = sessionStorage.getItem("emailUser");
    const response = await accountManagerService.changePasswordWith2FA({
      email,
      currentPassword,
      newPassword,
      twoFactorCode,
    });

    if (response.status === 200) {
      return true;
    }

    throw new Error("Falha ao alterar senha");
  } catch (error) {
    console.error("Error changing password with 2FA:", error);
    throw error;
  }
}

async function verify2FAForTransaction(email, code) {
  try {
    await getJWT();
    const response = await accountManagerService.verifyMFA({
      email: email,
      code: code,
    });

    if (response.data && response.status === 200) {
      return response.data;
    }
    throw new Error("Invalid 2FA verification");
  } catch (error) {
    console.error("Error during 2FA transaction verification:", error);
    throw error;
  }
}

async function completeLogin(email, password, twoFactorCode) {
  try {
    await getJWT();
    const user = await accountManagerService.login({
      email: email,
      password: password,
      twoFactorCode: twoFactorCode || undefined,
    });

    saveBaasBearerToken(user?.data);

    const accountStatus = await accountManagerService.showAccountStatus({
      email: email,
    });

    console.log("User data after complete login:", accountStatus);

    sessionStorage.setItem("emailUser", email);
    sessionStorage.setItem("type", "");
    // Grava sempre, inclusive lista vazia — mesma correção de verify2FA acima.
    persistServicesEnable(resolveServiceFlags(user?.data));
    // Task 30: `enable` legado numa chave própria, desacoplada de services_enable — mesmo
    // ponto de escrita de verify2FA acima.
    persistEnableLegacy(user?.data?.enable);
    sessionStorage.setItem("USD", "");
    sessionStorage.setItem("EUR", "");
    sessionStorage.setItem("JPY", "");
    sessionStorage.setItem("CAD", "");
    sessionStorage.removeItem("tempPassword");
    sessionStorage.removeItem("tempUserData");
    sessionStorage.removeItem("tempUserEmail");
    const expirationTime = new Date().getTime() + 30 * 60 * 1000;
    sessionStorage.setItem("sessionExpiration", expirationTime.toString());

    saveSessionFromAccountStatus(accountStatus, email);

    if (user.data.enable !== "RAMPS") {
      const bank = await accountManagerService.showBalance();
      const accountFromBalance =
        bank?.data?.account || bank?.data?.body?.account;
      const agencyFromBalance = bank?.data?.agency || bank?.data?.body?.agency;
      if (accountFromBalance) {
        sessionStorage.setItem("account", accountFromBalance);
      }
      if (agencyFromBalance) {
        sessionStorage.setItem("agency", agencyFromBalance);
      }
    }

    window.location.href = "/user";
  } catch (error) {
    console.error("Error during complete login:", error);
    throw error;
  }
}

const backendServices = {
  decodeQrCode,
  showPix,
  showPixKey,
  login,
  logout,
  showExternalPixKey,
  getJWT,
  validateAuth,
  verify2FA,
  createMFA,
  verifyMFASetup,
  changePasswordWith2FA,
  verify2FAForTransaction,
  completeLogin,
  checkSessionExpiration,
};

export default backendServices;

// Exportado à parte (correção rodada 1 da Task 23) para pages/Login/index.jsx reusar a MESMA
// lógica de ausência-vs-vazio em vez de reimplementá-la — foi exatamente a terceira cópia
// dessa lógica, divergente das outras duas, que abriu a brecha original.
export { resolveServiceFlags };
